import { z } from 'zod';
import { MAX_MESSAGES, MessageSchema, Rfc3339DateTime } from './create-chat.js';

/**
 * Shared, **versioned** wire contract for the `ui` BFF → `workstreamagent`
 * (Python) cross-module invoke boundary (D4-14 / ADR-0029 / ADR-0030).
 *
 * The BFF invokes the Python function's `live` alias synchronously
 * (`RequestResponse`) and validates the reply against {@link AgentInvokeResponseSchema}.
 * One source of truth both sides reason about — the BFF imports it directly; the
 * Python handler's echo shape is the same contract transcribed in Python.
 *
 * **Expand-contract (ADR-0029).** {@link AGENT_CONTRACT_VERSION} pins the request
 * shape; bump it only on a breaking change (additive fields don't bump it). The
 * **response** is **tolerant-out** (`z.looseObject`) so the agent module can add
 * fields and roll forward/back independently of the BFF — a new agent field never
 * breaks an old BFF, mirroring the ADR-0024 §11 response posture.
 *
 * NB (D4-13 reconciliation): the deployed Python function is a minimal structured
 * **echo** (no model/inference yet), so the response carries `echo` rather than a
 * score. When the real agentic handler lands, it adds fields under the same
 * tolerant-out response — no BFF change required, no version bump.
 */

/**
 * The BFF→Python invoke-contract version. A breaking change to
 * {@link AgentInvokeRequestSchema} bumps this; additive optional fields do not.
 */
export const AGENT_CONTRACT_VERSION = 1;

/** Generous cap on the free-text probe message — bounds the invoke payload (OWASP API4). */
const MAX_MESSAGE_LEN = 10_000;

/**
 * Attachment caps. The base64 length bounds the synchronous Lambda invoke
 * payload (6 MB cap) and keeps the decoded PDF under Bedrock Converse's
 * 4.5 MB per-document limit (both verified against AWS docs 2026-07-13):
 * 4.5M base64 chars ≈ 3.4 MB raw + a full 1 MB transcript still fits.
 */
export const MAX_ATTACHMENT_B64_LEN = 4_500_000;
/** Raw-byte sibling (¾ of the base64 cap) for the SPA's pre-encode file check. */
export const MAX_ATTACHMENT_BYTES = 3_375_000;

/**
 * A PDF the user attached to a chat turn. Turn-scoped, not persisted: the
 * agent runtime feeds it to the model as a Bedrock Converse document block
 * (via pydantic-ai `BinaryContent`) — no tool loop, no storage.
 * `data` is standard base64; the refine checks the `%PDF-` magic bytes so a
 * mislabelled file is rejected at the trust boundary, in the SPA pre-send
 * and at the BFF edge alike (`atob` is global in browsers and Node ≥ 16).
 */
// ponytail: turn-scoped base64 on the wire; move to S3 + presigned upload when
// attachments must persist or outgrow the invoke payload (concept doc Stage 3).
export const AttachmentSchema = z.strictObject({
  name: z.string().min(1).max(255),
  mediaType: z.literal('application/pdf'),
  data: z
    .base64()
    .min(16)
    .max(MAX_ATTACHMENT_B64_LEN)
    .refine((d) => {
      // Zod runs refinements even when the base64 check above failed, and
      // atob throws on invalid input — treat "won't decode" as "not a PDF".
      try {
        return atob(d.slice(0, 8)).startsWith('%PDF-');
      } catch {
        return false;
      }
    }, 'not a PDF (magic bytes)'),
});
export type Attachment = z.infer<typeof AttachmentSchema>;

/**
 * A deliverable an agent wrote this turn (ADR-0040) — the typed mirror of the
 * Python `ArtifactRecord`. `key` is the durable identity (the ADR-0039 S3
 * layout); `url` is a presigned GET — turn-scoped best-effort (it dies with
 * the agent Lambda's role session no matter the requested expiry); `preview`
 * is a capped text head (8k chars, text-family content types only) for
 * inline rendering without any browser→S3 fetch.
 */
export const TurnArtifactSchema = z.strictObject({
  filename: z.string().min(1).max(255),
  contentType: z.string().min(1).max(255),
  sizeBytes: z.number().int().nonnegative(),
  key: z.string().min(1),
  // Python serialises absent as null — accept both, normalise to undefined.
  url: z
    .url()
    .nullish()
    .transform((v) => v ?? undefined),
  preview: z
    .string()
    .nullish()
    .transform((v) => v ?? undefined),
});
export type TurnArtifact = z.infer<typeof TurnArtifactSchema>;

/**
 * The payload the BFF sends as the Lambda invoke event. Strict — the BFF authors
 * it, so an unexpected key is a bug. `requestId` doubles as the **idempotency /
 * correlation key** carried into the callee (the agent honours it once it becomes
 * stateful; synchronous `RequestResponse` is not auto-retried, so this is the
 * only dedup seam — D4-14 sync-client policy).
 */
export const AgentInvokeRequestSchema = z.strictObject({
  contractVersion: z.literal(AGENT_CONTRACT_VERSION),
  requestId: z.string().min(1),
  occurredAt: Rfc3339DateTime,
  message: z.string().min(1).max(MAX_MESSAGE_LEN),
  // Librarian extensions — additive, optional. Old callers omit them safely; no version bump.
  workstreamId: z.string().min(1).optional(),
  // `exitDiscovery` transitions a workstream from discovery mode to normal routing.
  // `pollTurn` reads an async turn's status/result (ADR-0037) — a cheap sync invoke.
  // `listArtifacts` / `getArtifact` / `deleteArtifact` are the Drop-6 Files API
  // (ADR-0045) — the BFF's artifact routes invoke these; the ownership check is
  // at the BFF, so the agent stays tenant-agnostic. `listMessages` is the Drop-7
  // transcript read path (ADR-0047) — reads `WorkstreamState.recent_messages`.
  // `deleteWorkstream` hard-deletes a whole workstream — the agent removes its
  // DynamoDB state item (chat + ledger) and every S3 artifact in the ledger; the
  // BFF is the sole ownership gate and deletes its own row AFTER the agent (so a
  // failed agent delete leaves the workstream intact + retryable, never orphaned).
  action: z
    .enum([
      'chat',
      'create',
      'ping',
      'exitDiscovery',
      'pollTurn',
      'listArtifacts',
      'getArtifact',
      'deleteArtifact',
      'deleteWorkstream',
      'listMessages',
    ])
    .optional(),
  // ADR-0046: the artifact to fetch/delete on `getArtifact` / `deleteArtifact` —
  // an OPAQUE per-artifact id (the uuid segment of the S3 key). The agent resolves
  // it to the durable key via THIS workstream's ledger, so it is inherently scoped
  // (an id absent from the ledger → not found) — no client-supplied key, no prefix
  // string-confinement, no `%2F`-in-path fragility (supersedes ADR-0045's
  // key-as-artifactId). Absent for `listArtifacts` (the whole workstream).
  artifactId: z.string().min(1).max(128).optional(),
  /**
   * ADR-0037: true on an `Event` (async) invoke — the agent claims + persists
   * the turn (`running` → `complete`/`failed`) instead of replying inline.
   */
  async: z.boolean().optional(),
  /** ADR-0037: the turn to read on `action: 'pollTurn'` (= the submit's requestId). */
  turnId: z.string().min(1).optional(),
  agentName: z.string().optional(),
  forcedAgents: z.array(z.string()).optional(),
  /**
   * ADR-0047: newest-N bound for `action: 'listMessages'` — the agent returns at
   * most this many of the newest messages (chronological order). The store caps
   * at 40 today; 100 matches the wire's MAX_MESSAGES ceiling.
   */
  limit: z.number().int().min(1).max(MAX_MESSAGES).optional(),
  // Full transcript for Router context — mirrors the Python contract's
  // `messages: list[Message] | None` (contracts/invoke.py). Missed in the
  // original port; added here to match.
  messages: z.array(MessageSchema).max(MAX_MESSAGES).optional(),
  // PDF attached to THIS turn only (additive-optional; mirrored in invoke.py).
  attachment: AttachmentSchema.optional(),
});
export type AgentInvokeRequest = z.infer<typeof AgentInvokeRequestSchema>;

/**
 * Lifecycle of an async turn (ADR-0037): claimed `running` on delivery,
 * resolved `complete`/`failed` by the agent; a `pollTurn` reads it back.
 */
export const TurnStatusSchema = z.enum(['running', 'complete', 'failed']);
export type TurnStatus = z.infer<typeof TurnStatusSchema>;

/**
 * The reply the Python function returns. **Tolerant-out** (`z.looseObject`): the
 * agent module may add fields and the BFF parse must survive them (expand-contract,
 * ADR-0029). Base fields are `ok`/`module`/`version`/`requestId`; pipeline fields
 * (`reply`, `consensus`) are optional extras the Librarian adds.
 */
export const AgentInvokeResponseSchema = z.looseObject({
  ok: z.boolean(),
  module: z.string(),
  version: z.string(),
  requestId: z.string(),
  echo: z.unknown().optional(),
  // Librarian pipeline extras — tolerant-out, BFF reads but never requires them
  reply: z.unknown().optional(),
  consensus: z.string().optional(),
  // Per-agent envelopes in execution order; the BFF narrows to `agent_name`s.
  agents: z.unknown().optional(),
  // Tracker detail when consensus is `conflicted` (plan flow step 8b).
  conflictDetail: z.string().nullable().optional(),
  // Deliverables written this turn (ADR-0040); the BFF narrows each entry
  // against TurnArtifactSchema, dropping malformed ones.
  artifacts: z.unknown().optional(),
  // Long-lived workstream state; the BFF narrows `mode` out of it (kept
  // z.unknown so a Python-side state-shape change never fails the parse).
  workstreamState: z.unknown().optional(),
  // ADR-0037: present on a `pollTurn` reply — `complete` carries the stored
  // pipeline fields above (reply/agents/consensus/…) alongside it.
  turnStatus: TurnStatusSchema.optional(),
  // ADR-0042: progress checkpoints on a `pollTurn` reply; the BFF narrows
  // each entry against TurnEventSchema, dropping malformed ones.
  events: z.unknown().optional(),
});
export type AgentInvokeResponse = z.infer<typeof AgentInvokeResponseSchema>;

/* ── HTTP ping route — POST /api/v1/workstream/agent/ping ────────────────── */

/**
 * Request body for the diagnostic ping route that exercises the BFF→Python seam.
 * `message` is optional (the BFF defaults it); strict so a stray key is rejected
 * at the edge (the same discipline as the create-flow requests).
 */
export const AgentPingRequestSchema = z.strictObject({
  message: z.string().min(1).max(MAX_MESSAGE_LEN).optional(),
});
export type AgentPingRequest = z.infer<typeof AgentPingRequestSchema>;

/**
 * The `data` payload of the ping response — the agent's (tolerant-out) reply.
 * `data` itself stays strict (fixed shape; the tolerance is on the nested
 * `agent` per the response contract above), wrapped in the standard `{ data, meta }`
 * envelope by the handler.
 */
export const AgentPingDataSchema = z.strictObject({
  agent: AgentInvokeResponseSchema,
});
export type AgentPingData = z.infer<typeof AgentPingDataSchema>;
